import { fromEvent, map } from 'rxjs';
import VMasker from 'vanilla-masker';

export default class PhoneInput {
  constructor() {
    this.valueInput = '';
    this.required = false;
    this.errorMessageId = 'nhs_ErrorForPhone';
    this.id = 'UserPhoneNumber';
    this.errorCssClass = 'error';
    this.labelClassName = 'nhs-u-text--visually-hidden';
    this.fieldClassName = 'form__input';
    this.errorClassName = 'form__error';
    this.labelText = 'Phone';
    this.pattern = '^\\([2-9][0-9]{2}\\)\\s[0-9]{3}-[0-9]{4}$';
    this.matchingPattern = new RegExp(this.pattern);
    this.screenReaderMessage = `<p id="mask-hidden-helper-msg" hidden>
                                  Only numbers are allowed. This field has masking, it will automatically format as
                                  you type.
                                </p>`;
  }

  set value(newValue){
    this.valueInput = newValue || '';
  }

  get value(){
    return this.valueInput;
  }

  /**
   * Overrides the CSS classes used by getHtml(). Defaults (set in the constructor) match every
   * existing consumer of this component; only screens that want different styling need to call
   * this — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {Object} classNames
   * @param {string} [classNames.labelClassName]
   * @param {string} [classNames.fieldClassName]
   * @param {string} [classNames.errorClassName]
   */
  setClassNames({ labelClassName, fieldClassName, errorClassName } = {}) {
    if (labelClassName !== undefined) {this.labelClassName = labelClassName;}
    if (fieldClassName !== undefined) {this.fieldClassName = fieldClassName;}
    if (errorClassName !== undefined) {this.errorClassName = errorClassName;}
    return this;
  }

  /**
   * Overrides the visible label text. Default (set in the constructor) matches every existing
   * consumer of this component; only screens that want different copy need to call this — see
   * GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} text
   */
  setLabelText(text) {
    this.labelText = text;
    return this;
  }

  /**
   * Overrides the input element's id used by getHtml()/getNode(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} id
   */
  setInputId(id) {
    this.id = id;
    return this;
  }

  /**
   * Overrides the error-message element's id used by getHtml(). Default (set in the constructor)
   * matches every existing consumer of this component; a screen that can render alongside another
   * instance of this component on the same page (e.g. a persistent sidebar form) must override this
   * to avoid a duplicate-id collision — see GetMoreInformationForm (NHS20-8178) for the first caller.
   * @param {string} errorMessageId
   */
  setErrorMessageId(errorMessageId) {
    this.errorMessageId = errorMessageId;
    return this;
  }

  bindEvents() {
    const phoneInput = this.getNode();
    VMasker(phoneInput).maskPattern('(999) 999-9999');
  }

  getHtml() {
    return `<label for="${this.id}" class="${this.labelClassName}">${this.labelText}</label><input data-qa="phone-input" data-segment-identity-data="phone" id="${this.id}" class="${this.fieldClassName}"
          placeholder="${this.required ? '*' : ''}Phone" maxlength="100" name="phone" autocomplete="tel"
          type="text" inputmode="numeric" aria-describedby="mask-hidden-helper-msg"
          value="${this.value}" ${this.required ? 'required' : ''} data-form-phone-input></input>
          <span id="${this.errorMessageId}" class="${this.errorClassName}" hidden>
                           Please enter a valid phone number
                         </span>${this.screenReaderMessage}`;
  }

  getNode() {
    return document.getElementById(this.id);
  }

  /**
   * getOnChangeEvent: On input change map its value to the storage property.
   */
  getOnChangeEvent() {
    return fromEvent(this.getNode(), 'blur').pipe(
      map((event) => ({phone: event.target.value}))
    );
  }

  removeRequired() {
    const node = this.getNode();
    node.placeholder = "Phone";
    node.removeAttribute('required');
  }

  setInvalidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.setAttribute('aria-invalid', true);
    node.classList.add(this.errorCssClass);
    errorMessage.removeAttribute('hidden');
  }

  setRequired() {
    const node = this.getNode();
    node.placeholder = "*Phone";
    node.setAttribute('required', 'true');
  }

  setValidState(node) {
    const errorMessage = document.getElementById(this.errorMessageId);

    node.removeAttribute('aria-invalid');
    node.classList.remove(this.errorCssClass);
    errorMessage.setAttribute('hidden', true);
  }

  isValid() {
    const node = this.getNode();
    const isEmpy = node.value === '';
    const isRequired = node.required;

    // If it is empty and is required, the input is not valid
    if(isEmpy && isRequired) {
      return false;
    }

    // If it is empty a is not required, the input is valid
    if (isEmpy && !isRequired) {
      return true;
    }

    // Otherwise the input is not empty and need to match with the regex.
    return this.matchingPattern.test(node.value);
  }

  validate() {
    const node = this.getNode();
    const isValid = this.isValid();

    return new Promise((resolve) => {
      if (isValid) {
        this.setValidState(node);
        resolve(true);
      } else {
        this.setInvalidState(node);
        resolve(false);
      }
    });
  }
}
